import type { I18nDictionaryShape } from '../../types';
import type { featuresEnGb } from '../en-GB/features';

export const featuresDeDe = {
  annotations: {
    aiFeedbackAlert: {
      desktopPrompt: 'Klicke mit der rechten Maustaste auf das Foto, um KI-Feedback zu geben!',
      improveAccuracy: 'Genauigkeit verbessern.',
      improveAccuracyFor: 'Genauigkeit für {{tradeCompanyName}} verbessern.',
      mobilePrompt: 'Tippe und halte das Foto, um KI-Feedback zu geben!',
    },
    aiFeedbackSwitch: {
      label: 'KI-Feedback anzeigen',
    },
    aiFilters: {
      analysisFailedDescription:
        'Unser Team sieht sich das an. Bitte fahre in der Zwischenzeit mit den übrigen Fotos im Bericht fort.',
      analysisFailedTitle: 'Die KI konnte dieses Foto nicht analysieren',
      description: 'Zeige oder verberge die Materialumrisse und vergleiche sie mit dem Originalfoto.',
      noFiltersDescription:
        'Die KI von Safi umrandet nur Nicht-Zielmaterial (wie Verunreinigungen oder Anhaftungen). Dieses Foto enthält nur Zielmaterial (z. B. UBC oder PET), daher gibt es keine Umrisse oder Filter zum Anzeigen.',
      noFiltersTitle: 'Keine Filter verfügbar, da 100 % Zielmaterial erkannt wurden',
      showOutlines: 'KI-Umrisse anzeigen',
      title: 'KI-Filter anpassen',
    },
    commentBubble: {
      actions: {
        deleteFeedback: 'Feedback löschen',
        edit: 'Bearbeiten',
      },
      adminMeta: 'ID:{{commentId}} KI:{{aiVersion}}',
      deleteDialog: {
        confirm: 'Löschen',
        message: 'Möchtest du dieses Feedback wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
        title: 'Feedback löschen',
      },
    },
    commentFormDrawer: {
      actions: {
        cancel: 'Abbrechen',
        save: 'Speichern',
      },
      feedbackType: {
        incorrectDetection: 'Objekt falsch erkannt',
        missingDetection: 'Objekt nicht erkannt',
      },
      labels: {
        note: 'Notiz',
      },
      titleCreate: 'KI-Feedback geben',
      titleEdit: 'KI-Feedback bearbeiten',
    },
    helpAndFeedbackBar: {
      downloadFullImageAriaLabel: 'Vollständiges Nachweisbild herunterladen',
    },
    segmentationOverlay: {
      ariaLabel: 'Bild mit Segmentierungsüberlagerung',
      helpText: {
        desktop: 'Bewege den Mauszeiger über das Foto, um zu zoomen',
        mobile: 'Mit den Fingern hineinzoomen',
      },
    },
    speechBubbleAvatar: {
      viewCommentAriaLabel: 'Kommentar von {{fullName}} ansehen',
    },
  },
  auth: {
    companySignUpForm: {
      actions: {
        next: 'Weiter',
      },
      labels: {
        companyType: 'Unternehmenstyp',
        country: 'Land',
        name: 'Name',
      },
      notifications: {
        errorSigningUp: 'Fehler bei der Registrierung',
      },
      validation: {
        companyTypeRequired: 'Unternehmenstyp ist erforderlich',
        countryRequired: 'Land ist erforderlich',
        nameRequired: 'Name ist erforderlich',
      },
    },
    otpForm: {
      actions: {
        signIn: 'Anmelden',
      },
      labels: {
        passcode: 'Code',
      },
      validation: {
        digitsOnly: 'Darf nur Ziffern enthalten',
        passcodeLength: 'Der Einmalcode muss 4 Ziffern haben',
        passcodeRequired: 'Einmalcode ist erforderlich',
      },
    },
    signInEmailForm: {
      actions: {
        next: 'Weiter',
      },
      labels: {
        email: 'E-Mail',
      },
      links: {
        signInWithPhone: 'Mit Telefonnummer anmelden',
      },
      validation: {
        emailRequired: 'E-Mail ist erforderlich',
        invalidEmail: 'Ungültige E-Mail',
        termsRequired: 'Du musst die Allgemeinen Geschäftsbedingungen akzeptieren',
      },
    },
    signInPage: {
      subtitle:
        'Melde dich bei {{brandName}} mit deiner geschäftlichen E-Mail oder Telefonnummer an. Wir senden dir einen Einmalcode für einen schnelleren Zugriff.',
      title: 'Qualitätskontrolle mit Vertraün',
    },
    signInPhoneForm: {
      actions: {
        next: 'Weiter',
        signInWithEmail: 'Mit E-Mail anmelden',
      },
      labels: {
        phoneNumber: 'Telefonnummer',
      },
      validation: {
        phoneNumberRequired: 'Telefonnummer ist erforderlich',
      },
    },
    termsAndPolicyAgreement: {
      fullSentence:
        'Ich akzeptiere die <termsLink>Allgemeinen Geschäftsbedingungen</termsLink> und die <privacyLink>Datenschutzerklärung</privacyLink>, indem ich {{brandName}} beitrete.',
    },
    userSignUpForm: {
      actions: {
        next: 'Weiter',
      },
      labels: {
        email: 'E-Mail',
        fullName: 'Vollständiger Name',
        phoneNumber: 'Telefonnummer',
      },
      notifications: {
        errorSigningUp: 'Fehler bei der Registrierung',
      },
      validation: {
        emailRequired: 'E-Mail ist erforderlich',
        fullNameRequired: 'Vollständiger Name ist erforderlich',
        invalidEmail: 'Ungültige E-Mail',
        phoneNumberRequired: 'Telefonnummer ist erforderlich',
        termsRequired: 'Du musst die Allgemeinen Geschäftsbedingungen akzeptieren',
      },
    },
  },
  backlog: {
    breakdowns: {
      evaluatedSpecsItems: {
        ungroupedMaterials: 'Nicht gruppierte Materialien',
      },
      formatDirtScore: {
        average: 'Durchschnittlich',
        clean: 'Sauber',
        dirty: 'Schmutzig',
        notAvailable: 'k. A.',
        veryClean: 'Sehr sauber',
      },
      materialAiBreakdown: {
        aiModelNotSupported: 'KI-Modell wird nicht unterstützt',
      },
      materialLabels: {
        aluminium: 'Aluminium',
        aluminiumAerosol: 'Aluminium-Aerosol',
        aluminiumFoodCan: 'Aluminium-Lebensmitteldose',
        brown: 'Braun',
        clear: 'Klar',
        clearNonFoodGrade: 'Klar, nicht lebensmitteltauglich',
        clearPetTray: 'Klare PET-Schale',
        petTrayContaminatedClear: 'Verunreinigte klare PET-Schale',
        darkBlue: 'Dunkelblau',
        dirtyBottles: 'Verschmutzte Flaschen',
        expandedPolystyrene: 'Expandiertes Polystyrol',
        film: 'Folie',
        foilAndTray: 'Folie und Schale',
        fullSleeve: 'Vollflächige Sleeve',
        green: 'Grün',
        hdpe: 'HDPE',
        hdpeJazz: 'Jazz',
        hdpeNaturalFoodGrade: 'Natur, lebensmitteltauglich',
        hdpeNaturalNonFoodGrade: 'Natur, nicht lebensmitteltauglich',
        hdpeSiliconTubes: 'Silikonschläuche',
        hdpeWhiteFoodGrade: 'Weiß, lebensmitteltauglich',
        hdpeWhiteNonFoodGrade: 'Weiß, nicht lebensmitteltauglich',
        lightBlue: 'Hellblau',
        metal: 'Metall',
        nonAluminium: 'Kein Aluminium',
        nonHdpe: 'Kein HDPE',
        nonMetal: 'Kein Metall',
        nonPet: 'Kein PET',
        nonPlastic: 'Kein Kunststoff',
        oilBottle: 'Ölflasche',
        other: 'Sonstiges',
        otherAluminium: 'Anderes Aluminium',
        otherColour: 'Andere Farbe',
        otherMetal: 'Anderes Metall',
        otherPlastic: 'Anderer Kunststoff',
        pet: 'PET',
        polypropylene: 'PP',
        prohibitive: 'Nicht zulässig',
        total: 'Gesamt',
        ubc: 'UBC',
        waste: 'Abfall',
        white: 'Weiß',
      },
      representativeEvidenceFilter: {
        label: 'Bilder anzeigen von:',
        options: {
          bestBales: 'Beste Ballen',
          typicalBales: 'Typische Ballen',
          worstBales: 'Schlechteste Ballen',
        },
      },
      representativeEvidenceSection: {
        distributionTitle: '{{targetName}} Verteilung',
      },
      specificationAlerts: {
        groupDescription:
          '<boldAction>{{actionText}}</boldAction> - Die Gruppe <boldName>{{name}}</boldName> ist <boldComparison>{{comparison}}</boldComparison>',
        groupDescriptionWithoutComparison:
          '<boldAction>{{actionText}}</boldAction> - Die Gruppe <boldName>{{name}}</boldName> ist',
        groupIs: 'Gruppe ist',
        the: '- Die',
        title: 'Warnungen',
      },
      totalWeightWarning: {
        message:
          'Warnung! Das Gesamtgewicht ({{formattedTotalWeight}}kg) entspricht nicht der Summe der Aufschlüsselungsgewichte ({{formattedSum}}kg).',
      },
    },
    cameraGuidance: {
      useCameraStream: {
        notifications: {
          cannotUseCamera: 'Kamera kann nicht verwendet werden',
          refreshAndAllowCamera:
            'Aktualisiere diese Seite und versuche es erneut. Erlaube den Kamerazugriff, wenn du dazu aufgefordert wirst.',
        },
      },
    },
    common: {
      noData: 'Keine Daten',
    },
    dealDetails: {
      fields: {
        destination: 'Zielort',
        endProcessor: 'Endverarbeiter',
        inspections: 'Inspektionen',
        loadingSite: 'Verladestandort',
        loads: 'Ladungen',
        material: 'Material',
        producer: 'Erzeuger',
        risk: 'Risiko',
      },
      header: {
        deal: 'Deal {{dealId}}',
      },
    },
    dealRevenueDetails: {
      columns: {
        description: 'Beschreibung',
        id: 'ID',
        invoiceStatus: 'Rechnungsstatus',
        invoiceXeroAmountPaid: 'In Xero bezahlter Rechnungsbetrag',
        invoiceXeroFullyPaidOn: 'In Xero vollständig bezahlt am',
        invoiceXeroId: 'Xero-Rechnungs-ID',
        lineTotal: 'Zeilensumme',
        quantity: 'Menge',
        unitAmount: 'Einzelbetrag',
      },
    },
    documentGeneration: {
      notifications: {
        failedToSaveFile: 'Datei konnte nicht gespeichert werden',
        failedToUploadFile: 'Datei konnte nicht hochgeladen werden',
      },
    },
    evidence: {
      baleEvidenceGallery: {
        analysingMultiplePhotos: '{{count}} Fotos werden analysiert...',
        analysingSinglePhoto: '{{count}} Foto wird analysiert...',
        noEvidenceFound: 'Keine Fotos gefunden',
      },
      bulkDownload: {
        empty: 'Keine Nachweise zum Herunterladen',
        error: 'Herunterladen der Nachweise fehlgeschlagen',
        inProgress: 'Download wird vorbereitet…',
        partialSuccess: '{{downloaded}} von {{total}} Fotos heruntergeladen',
        success: '{{count}} Fotos heruntergeladen',
      },
      common: {
        editSample: 'Probe bearbeiten',
        manageInspection: 'Inspektion verwalten',
      },
      evidenceAiDetails: {
        aiAnalysisTitle: 'KI-Analyse',
        aiImagesTitle: 'KI-Bilder',
      },
      evidenceAiLooseSessionDetails: {
        aiLooseAnalysisTitle: 'KI-Lose-Analyse',
        sampleImagesTitle: 'Probenbilder',
      },
      evidencePhotosViewer: {
        bulkDeleteError: 'Fotos konnten nicht gelöscht werden',
        bulkDeleteSuccess: '{{count}} Fotos erfolgreich gelöscht',
      },
      loadingPhotoEvidenceGallery: {
        noOtherImagesAvailable: 'Keine weiteren Bilder verfügbar.',
      },
      manualBreakdownFormFields: {
        kilogramUnit: 'Kg',
      },
      sessionEditDialog: {
        actions: {
          editDeleteEvidence: 'Nachweise bearbeiten / löschen',
        },
      },
      sessionEvidenceEditDialog: {
        step1Title: 'Schritt 1. Wähle die Nachweise aus, die du ändern möchtest',
        step2Title: 'Schritt 2. Gib an, wie die ausgewählten Nachweise geändert werden sollen',
      },
      sessionSummary: {
        anonymous: 'Anonym',
        withOrigin: '{{origin}} - Hochgeladen von {{uploadedBy}}',
        withoutOrigin: 'Hochgeladen von {{uploadedBy}}',
      },
      uploadMenu: {
        downloadAllEvidence: 'Alle Nachweise herunterladen',
        downloadingAllEvidence: 'Herunterladen…',
        editEvidence: 'Nachweise bearbeiten',
        menuAriaLabel: 'Weitere Aktionen',
        offlineModeComingSoon: 'Offline-Modus kommt bald',
        openCamera: 'Kamera öffnen',
        uploadEvidence: 'Nachweise hochladen',
      },
      uploadOptionsButton: {
        actions: {
          upload: 'Hochladen',
        },
        options: {
          openCamera: 'Kamera öffnen',
          uploadPhoto: 'Foto hochladen',
        },
      },
    },
    evidenceGrid: {
      columns: {
        evidence: 'Nachweis',
      },
    },
    evidenceUploadButton: {
      label: 'Hochladen',
      notifications: {
        failedToUploadEvidence: 'Nachweis konnte nicht hochgeladen werden',
        failedToUploadPhoto: '{{photoName}} konnte nicht hochgeladen werden: {{error}}',
        successfullyUploadedEvidence: 'Nachweis erfolgreich hochgeladen',
      },
    },
    externalLoad: {
      common: {
        supplier: 'Lieferant',
      },
    },
    findTicketsTable: {
      columns: {
        category: 'Kategorie',
        status: 'Status',
        summary: 'Zusammenfassung',
        team: 'Team',
      },
    },
    firmQuoteBulkForm: {
      columns: {
        bunkerAdjustmentFactorCost: 'Kosten für Bunkerzuschlagsfaktor',
        bunkerAdjustmentFactorCurrency: 'Währung des Bunkerzuschlagsfaktors',
        carrier: 'Frachtführer',
        containerType: 'Containertyp',
        demandIncoterm: 'Demand-Incoterm',
        destinationCountry: 'Zielland',
        destinationLat: 'Breitengrad Zielort',
        destinationLon: 'Längengrad Zielort',
        destinationName: 'Name Zielort',
        directLandCost: 'Direkte Landkosten',
        directLandCurrency: 'Währung direkte Landkosten',
        e2eCost: 'E2E-Kosten',
        e2eCurrency: 'E2E-Währung',
        estimatedDeparture: 'Vorauss. Abfahrt',
        expiresOn: 'Gültig bis',
        forwarder: 'Spediteur',
        notes: 'Notizen',
        oceanFreightCost: 'Kosten Seefracht',
        oceanFreightCurrency: 'Währung Seefracht',
        onCarriageCost: 'Nachlaufkosten',
        onCarriageCurrency: 'Währung Nachlauf',
        originCountry: 'Ursprungsland',
        originLat: 'Breitengrad Ursprung',
        originLon: 'Längengrad Ursprung',
        originName: 'Name Ursprung',
        peakSeasonSurchargeCost: 'Hochsaisonzuschlag Kosten',
        peakSeasonSurchargeCurrency: 'Hochsaisonzuschlag Währung',
        portOfDischarge: 'Entladehafen',
        portOfLoading: 'Verladehafen',
        preCarriageCost: 'Vorlaufkosten',
        preCarriageCurrency: 'Währung Vorlauf',
        quotedOn: 'Angeboten am',
        supplyIncoterm: 'Supply-Incoterm',
        typeOfGoods: 'Warenart',
      },
    },
    grade: {
      compositionReport: {
        annotatedBaleAlt: 'Annotierter Ballen',
      },
      imageQualityPrompt: {
        label: 'Erneut aufnehmen',
      },
    },
    groundControl: {
      common: {
        descriptions: {
          calculateDealProfitability: 'Deal-Rentabilität berechnen',
          createPurchaseOrderAndSendToSeller:
            'Einen Einkaufsauftrag erstellen und zur Unterschrift an den Verkäufer senden',
          createSalesOrderAndSendToBuyer: 'Einen Verkaufsauftrag erstellen und zur Unterschrift an den Käufer senden',
          viewEditAndMatch: 'Ansehen, bearbeiten und abgleichen',
        },
        labels: {
          createPurchaseOrder: 'Einkaufsauftrag erstellen',
          createSalesOrder: 'Verkaufsauftrag erstellen',
          dealProfitabilityCalculator: 'Deal-Rentabilitätsrechner',
          viewPurchaseOrders: 'Einkaufsaufträge anzeigen',
          viewSalesOrders: 'Verkaufsaufträge anzeigen',
        },
      },
      tabs: {
        advanced: {
          label: 'Erweitert',
          links: {
            manageMaterialProducts: {
              description: 'Materialprodukte ansehen und bearbeiten',
              label: 'Materialprodukte verwalten',
            },
            pernType: {
              description: 'PERN-Typen aktualisieren',
              label: 'PERN-Typ',
            },
          },
        },
        contracts: {
          label: 'Verträge',
        },
        deals: {
          label: 'Deals',
          links: {
            goToDealHub: {
              description: 'Deal-Aufgaben ansehen und bearbeiten',
              label: 'Zu DealHub',
            },
            viewAllDeals: {
              description: 'Alle Deals und Ladungen ansehen und bearbeiten',
              label: 'Alle Deals anzeigen',
            },
          },
        },
        finance: {
          label: 'Finanzen',
          links: {
            createShippingRate: {
              description: 'Transporttarif und alle zugehörigen Details erstellen',
              label: 'Transporttarif erstellen',
            },
            manageBanks: {
              description: 'Bankdaten ansehen und bearbeiten',
              label: 'Banken verwalten',
            },
            manageCosts: {
              description: 'Kosten erstellen, bearbeiten und anzeigen',
              label: 'Kosten verwalten',
            },
            manageExternalCompanies: {
              description: 'Daten externer Unternehmen erstellen, bearbeiten und anzeigen',
              label: 'Externe Unternehmen verwalten',
            },
            manageFinancing: {
              description: 'Zahlungsanfragen an Finmid senden',
              label: 'Finanzierung verwalten',
            },
            manageRevenue: {
              description: 'Umsätze erstellen, bearbeiten und anzeigen',
              label: 'Umsätze verwalten',
            },
            marketPernPrices: {
              description: 'Historische PERN-Preise von T2E anzeigen',
              label: 'Markt-PERN-Preise',
            },
          },
        },
        inspections: {
          label: 'Inspektionen',
          links: {
            addExternalEvidence: {
              description: 'Nachweise aus externen Qüllen hinzufügen',
              label: 'Externe Nachweise hinzufügen',
            },
            addSiteEvidence: {
              description: 'Neü Standortnachweise hinzufügen',
              label: 'Standortnachweise hinzufügen',
            },
            createInspection: {
              description: 'Neü Inspektion erstellen',
              label: 'Inspektion erstellen',
            },
            inspectionCompanies: {
              description: 'Inspektionsunternehmen ansehen und bearbeiten',
              label: 'Inspektionsunternehmen',
            },
            inspections: {
              description: 'Inspektionen ansehen und bearbeiten',
              label: 'Inspektionen',
            },
            inspectors: {
              description: 'Inspektoren ansehen und bearbeiten',
              label: 'Inspektoren',
            },
            viewUpcomingLoads: {
              description: 'Bevorstehende Ladungen ansehen und bearbeiten',
              label: 'Bevorstehende Ladungen anzeigen',
            },
          },
        },
        support: {
          label: 'Support',
          links: {
            getSupport: {
              description: 'Ein Jira-Support-Ticket erstellen',
              label: 'Support erhalten',
            },
            helpAndLearn: {
              description: 'Hilfeführer',
              label: 'Hilfe & Lernen',
            },
          },
        },
        trade: {
          label: 'Handel',
          links: {
            createDemandSignal: {
              description: 'Ein Bedarfssignal erstellen. Es wird mit HubSpot synchronisiert',
              label: 'Bedarfssignal erstellen',
            },
            createSupplySignal: {
              description: 'Ein Angebotssignal erstellen. Es wird mit HubSpot synchronisiert',
              label: 'Angebotssignal erstellen',
            },
            generateDocuments: {
              description: 'Dokumente für Einkaufsaufträge, Verkaufsaufträge, Sendungen usw. erstellen',
              label: 'Dokumente erstellen',
            },
            qualityReport: {
              description: 'Daten und Bilder für Qualitätsberichte hinzufügen',
              label: 'Qualitätsbericht',
            },
            transportMetabase: {
              description: 'Link zur Transport-Metabase',
              label: 'Transport-Metabase',
            },
          },
        },
        transport: {
          label: 'Transport',
          links: {
            manageCarriers: {
              description: 'Frachtführer erstellen, bearbeiten und anzeigen',
              label: 'Frachtführer verwalten',
            },
            syncTransportCosts: {
              description: 'Transportkosten aus Tabellen synchronisieren',
              label: 'Transportkosten synchronisieren',
            },
            viewPorts: {
              description: 'Alle Häfen anzeigen',
              label: 'Häfen anzeigen',
            },
          },
        },
        usersAndCompanies: {
          label: 'Benutzer & Unternehmen',
          links: {
            copySessions: {
              description: 'Sitzungen per CSV-Upload in ML Accuracy Testing kopieren',
              label: 'Sitzungen kopieren',
            },
            createUserAndCompany: {
              description:
                'Einen Benutzer und ein neues Unternehmen erstellen oder zu einem bestehenden Unternehmen einladen',
              label: 'Benutzer und Unternehmen erstellen',
            },
            hijackUser: {
              description: 'Schnell ein Benutzerkonto übernehmen',
              label: 'Benutzer übernehmen',
            },
            staffOnboarding: {
              description: 'Einen neuen Admin-Benutzer erstellen und ihn zu Safi hinzufügen',
              label: 'Mitarbeiter-Onboarding',
            },
            viewAllUsers: {
              description: 'Alle Benutzer in HubSpot anzeigen',
              label: 'Alle Benutzer anzeigen',
            },
            viewTradeCompanies: {
              description: 'Angebots- und Nachfrage-Handelsunternehmen anzeigen und bearbeiten',
              label: 'Alle Handelsunternehmen anzeigen',
            },
          },
        },
      },
    },
    guidePriceChip: {
      label: 'Richtpreis',
      unavailable: 'Richtpreis nicht verfügbar',
    },
    helpGuides: {
      aiBale: {
        partOne: {
          boldText: 'Max. 1 Meter entfernt stehen',
          imageAlt: 'Maximal 1 Meter entfernt stehen',
          trailingText: 'vor dem Stapel.',
        },
      },
      aiLoose: {
        partFour: {
          and: 'und',
          clearPile: 'Den Haufen beiseite räumen',
          imageAlt: 'Haufen beiseite geräumt',
          repeatSteps: 'die Schritte 1-3 wiederholen',
          untilText: 'bis du für die gesamte Probe ein Foto pro Haufen aufgenommen hast.',
        },
        partOne: {
          imageAlt: 'Einen Haufen bilden',
          makePile: 'Einen Haufen bilden',
          middleText: 'aus deiner Probe und ihn gleichmäßig auf dem Boden verteilen.',
          takePhoto: '1 Foto pro Haufen aufnehmen.',
        },
        partThree: {
          boldIntro: 'Prüfe das Foto, bevor du den Haufen beiseite räumst.',
          imageAlt: 'Prüfen und löschen',
          trailingText: 'Wenn es nicht korrekt ist, kannst du es löschen und ein neues aufnehmen.',
          warningBold: 'Nur 1 Foto pro Haufen',
          warningPrefix: 'Lade',
          warningSuffix: 'hoch, damit die KI-Ergebnisse nicht verfälscht werden.',
        },
      },
      common: {
        completeAlt: 'Abgeschlossen',
      },
      data: {
        guides: {
          aiBale: 'KI-Ballen',
          aiBriquette: 'KI-Brikett',
          aiLoose: 'KI-Lose',
        },
        sections: {
          qualityControlGuides: 'Leitfäden für die Qualitätskontrolle',
        },
        subcategories: {
          howToTakeGreatAiPhotos: 'Wie man gute KI-Fotos aufnimmt',
        },
      },
    },
    inspectionMutate: {
      cancellationReasons: {
        dealCancelled: 'Deal storniert',
        missedLoading: 'Verladung verpasst',
        noCoverage: 'Keine Abdeckung',
        noInspector: 'Kein Inspektor',
        notNeeded: 'Nicht erforderlich',
        other: 'Sonstiges',
        siteRefused: 'Standort hat abgelehnt',
      },
      inspectionResults: {
        failed: 'Fehlgeschlagen',
        passed: 'Bestanden',
        pending: 'Ausstehend',
      },
      inspectionStatuses: {
        backlog: 'Backlog',
        booked: 'Gebucht',
        cancelled: 'Storniert',
        complete: 'Abgeschlossen',
        planning: 'In Planung',
        scheduled: 'Geplant',
      },
      inspectionTypes: {
        pern: 'Pern',
        sirim: 'Sirim',
        standard: 'Standard',
        supplier: 'Lieferant',
      },
    },
    inspectionsList: {
      columns: {
        company: 'Unternehmen',
        deal: 'Deal',
        endProcessor: 'Endverarbeiter',
        inspector: 'Inspektor',
        loads: 'Ladungen',
        material: 'Material',
        producer: 'Erzeuger',
        risk: 'Risiko',
        slug: 'Slug',
        status: 'Status',
        time: 'Zeit',
      },
      values: {
        notAvailable: 'k. A.',
      },
    },
    loadDetails: {
      actions: {
        notNeeded: 'Nicht erforderlich',
      },
      fields: {
        collectionTime: 'Abholzeit',
        inspection: 'Inspektion',
        status: 'Status',
      },
      values: {
        hasInspection: 'Mit Inspektion',
        noInspection: 'Ohne Inspektion',
      },
    },
    loadsList: {
      columns: {
        collectionTime: 'Abholzeit',
        deal: 'Deal',
        destination: 'Zielort',
        endProcessor: 'Endverarbeiter',
        hasInspection: 'Mit Inspektion',
        load: 'Ladung',
        loadingSite: 'Verladestandort',
        material: 'Material',
        producer: 'Erzeuger',
        risk: 'Risiko',
        status: 'Status',
      },
      values: {
        no: 'Nein',
        notAvailable: 'k. A.',
        yes: 'Ja',
      },
    },
    manageDataButton: {
      buttonLabel: 'Daten verwalten',
      options: {
        aiBalePhotos: 'KI-Ballenfotos',
        loadingEvidence: 'Verladenachweise',
        marketingMedia: 'Marketingmedien',
        samples: 'Proben',
        siteEvidence: 'Standortnachweise',
        specifications: 'Spezifikationen',
      },
    },
    materialCategoryActions: {
      notifications: {
        cannotSaveMaterial: 'Material kann nicht gespeichert werden',
        errorCreatingMaterial: 'Fehler beim Erstellen des Materials',
        errorDeletingMaterial: 'Fehler beim Löschen des Materials',
        errorSavingMaterial: 'Fehler beim Speichern des Materials',
        savedSuccessfully: 'Erfolgreich gespeichert',
        selectedTargetGroupHasNoOperands:
          'Die ausgewählte Zielgruppe hat keine Operanden. Bitte wähle eine andere Zielgruppe.',
        skippedSpecifications:
          '{{count}} Spezifikation(en) wurden nicht gespeichert, weil ihnen keine Operanden zugewiesen sind.',
      },
    },
    materialComponents: {
      supportedComponentList: {
        buttonText: 'Materialkomponente hinzufügen',
        title: 'Materialkomponenten',
      },
    },
    materialDirtLevel: {
      options: {
        clean: 'Sauber',
        dirty: 'Schmutzig',
        notAvailable: 'k. A.',
        veryClean: 'Sehr sauber',
      },
    },
    materialProducts: {
      validation: {
        atLeastOneBuyerMaterialComponentIsRequired: 'Mindestens eine Materialkomponente des Käufers ist erforderlich.',
        atLeastOneSellerMaterialComponentIsRequired:
          'Mindestens eine Materialkomponente des Verkäufers ist erforderlich.',
        buyerAndSellerMaterialComponentCountsMustMatch:
          'Die Anzahl der Werte für Materialkomponenten von Käufer und Verkäufer muss gleich sein.',
      },
    },
    materialProductsList: {
      columns: {
        buyerMaterialComponents: 'Materialkomponenten des Käufers',
        capsAndLabels: 'Deckel und Etiketten',
        country: 'Land',
        foodGrade: 'Lebensmitteltauglich',
        id: 'ID',
        materialGrade: 'Materialqualität',
        materialOrigin: 'Materialherkunft',
        materialSpec: 'Materialspezifikation',
        process: 'Prozess',
        sellerMaterialComponents: 'Materialkomponenten des Verkäufers',
      },
      values: {
        no: 'Nein',
        notSpecified: 'k. A.',
        yes: 'Ja',
      },
    },
    notificationParty: {
      label: 'Benachrichtigungsempfänger',
      options: {
        allViaAgent: 'Alle Kommunikation über den Agenten',
        directWithBuyer: 'Direkt mit dem Käufer kommunizieren',
      },
    },
    orders: {
      validation: {
        fixedPriceAndDateMustBeProvidedTogether: 'Festpreis und Fixdatum müssen gemeinsam angegeben werden',
      },
    },
    paymentMethod: {
      label: 'Zahlungsmethode',
      options: {
        documentAgainstPayment: 'Dokumente gegen Zahlung',
        telegraphicTransfer: 'Telegrafische Überweisung',
      },
    },
    percentageLightBlue: {
      label: 'Anteil hellblau',
      options: {
        notAvailable: 'k. A.',
      },
    },
    qualityReport: {
      evidenceTabs: {
        aiBaleAnalysis: 'KI-Ballenanalyse',
        aiLooseAnalysis: 'KI-Lose-Analyse',
        loading: 'Verladung',
        manualWeightSample: 'Manuelle Gewichtsprobe',
        marketing: 'Marketing',
        materialSpecification: 'Materialspezifikation',
        site: 'Standort',
      },
      siteMaterialQualityReportHeader: {
        evidencePhotoAlt: 'Nachweisfoto',
      },
    },
    qualityReportSummary: {
      titles: {
        availableMaterial: 'Verfügbares Material',
        deliveryLocation: 'Lieferort',
        estimatedDelivery: 'Geschätzte Lieferung',
        location: 'Standort',
        material: 'Material',
        materialGrade: 'Materialqualität',
        tonnesPerLoad: 'Tonnen pro Ladung',
      },
    },
    siteMaterials: {
      notifications: {
        errorCreatingMaterial: 'Fehler beim Erstellen des Materials',
        materialCreated: 'Material erstellt',
        productLinkedSuccessfully: 'Produkt erfolgreich verknüpft',
      },
    },
    timeFilter: {
      options: {
        last30Days: 'Letzte 30 Tage',
        last7Days: 'Letzte 7 Tage',
        last90Days: 'Letzte 90 Tage',
        next14Days: 'Nächste 14 Tage',
        next2Days: 'Nächste 2 Tage',
        next7Days: 'Nächste 7 Tage',
      },
    },
    variablePricingFields: {
      indexOptions: {
        lmeAluminium: 'LME Aluminium',
        lmeAluminiumMidwestPremium: 'LME Aluminium + Midwest Premium',
      },
    },
  },
  breakdowns: {
    adminAiVersionTitle: {
      value: 'KI:{{version}}',
    },
    aiSampleBreakdown: {
      analysisNotAvailable: 'Analyse nicht verfügbar',
    },
    aluMaterialSampleBreakdown: {
      labels: {
        aluminium: 'Aluminium',
        foilAndTray: 'Folie und Schale',
        foodCan: 'Lebensmitteldose',
        nonAluminium: 'Kein Aluminium',
        nonMetal: 'Kein Metall',
        otherAluminium: 'Anderes Aluminium',
        otherMetal: 'Anderes Metall',
        total: 'Gesamt',
        ubc: 'UBC',
      },
    },
    cleanliness: {
      label: 'Sauberkeit',
      tooltip:
        'Sauberkeit misst, wie sauber das Material ist. Die Skala reicht von sehr sauber über sauber und durchschnittlich bis schmutzig. Schmutz umfasst Fett, Flecken und Staub auf der Materialoberfläche.',
    },
    evaluatedSpecsBreakdown: {
      customizeTableFor: 'Tabelle anpassen für',
      targetChip: 'Ziel',
      thisMaterial: 'dieses Material',
      title: 'KI-Analyse',
    },
    noAnalysisAlert: {
      description:
        'Die KI konnte die Fotos nicht analysieren. Du kannst den Grund bei jedem Foto prüfen, die abgelehnten löschen und es erneut versuchen.',
      guidance: 'Sieh dir den <guideLink>{{guideLabel}}-Leitfaden</guideLink> für Tipps zu besseren Fotos an.',
      noSubmittedPhotosDescription:
        'Es wurden noch keine Fotos zur Analyse eingereicht. Füge Fotos hinzu, um die Analyse zu starten.',
      title: 'Keine Ergebnisse verfügbar',
    },
    analysisInProgressAlert: {
      pendingTitle: 'Analyse läuft',
      pendingDescription:
        '{{pendingCount}} von {{submittedCount}} Fotos werden noch analysiert. Ergebnisse sollten in wenigen Minuten erscheinen.',
      partialTitle: 'Weitere Fotos werden analysiert',
      partialDescription:
        'Die Ergebnisse unten basieren auf {{acceptedCount}} von {{submittedCount}} Fotos. {{pendingCount}} weitere werden noch analysiert.',
      rejectedDescription: '{{rejectedCount}} Fotos wurden abgelehnt. Prüfe den Grund bei jedem Foto.',
    },
    petFlake: {
      fields: {
        bulkDensity: 'Schüttdichte',
        clearFlakes: 'Klare Flakes',
        colouredFlakes: 'Farbige Flakes',
        darkBlueFlakes: 'Dunkelblaue Flakes',
        ferrousMetal: 'Eisenhaltiges Metall',
        glue: 'Kleber',
        greenFlakes: 'Grüne Flakes',
        intrinsicViscosity: 'Intrinsische Viskosität',
        isophthalicAcid: 'Isophthalsäure',
        lightBlueFlakes: 'Hellblaue Flakes',
        meltFlowRate: 'Schmelzflussrate (MF) (260 °C/2,16 kg)',
        meltingPoint: 'Schmelzpunkt',
        nonFerrousMetal: 'Nichteisenmetall',
        otherImpurities: 'Sonstige Verunreinigungen',
        paper: 'Papier',
        particlesSizeBetween10And12mm: 'Partikel mit einer Größe zwischen 10 und 12 mm',
        particlesSizeBetween5And10mm: 'Partikel mit einer Größe zwischen 5 und 10 mm',
        particlesSizeLt5mm: 'Partikel mit einer Größe < 5 mm',
        petContent: 'Polyethylenterephthalat-(PET)-Gehalt',
        ph: 'pH-Wert',
        polyolefinContamination: 'Polyolefin-Verunreinigung',
        pvcContamination: 'PVC-Verunreinigung',
        residualContamination: 'Restverunreinigung',
        residualMoisture: 'Restfeuchte',
        sampledBy: 'Beprobt von',
        woodenMaterials: 'Holzmaterialien',
        yellowFlakes: 'Gelbe Flakes',
      },
    },
    petMaterialSampleBreakdown: {
      labels: {
        clear: 'Klar',
        clearNonFoodGrade: 'Klar, nicht lebensmitteltauglich',
        clearPetTray: 'Klare PET-Schale',
        darkBlue: 'Dunkelblau',
        film: 'Folie',
        green: 'Grün',
        largeClear5L: 'Groß, klar (≥5L)',
        lightBlue: 'Hellblau',
        liquid: 'Flüssigkeit',
        nonPetBottle: 'Nicht-PET-Flasche',
        nonPlastic: 'Kein Kunststoff',
        other: 'Sonstiges',
        otherPlastic: 'Anderer Kunststoff',
        petBottle: 'PET-Flasche',
        prohibitive: 'Nicht zulässig',
        total: 'Gesamt',
      },
    },
    taintTaborMaterialSampleBreakdown: {
      labels: {
        aluIron: 'Alu./Eisen',
        aluNonMetal: 'Alu./Nicht-Metall',
        aluWithAttachments: 'Alu. mit Anhaftungen',
        aluminium: 'Aluminium',
        cast: 'Guss',
        castIron: 'Guss/Eisen',
        ferrousMetal: 'Eisenhaltiges Metall',
        finesLt20mm: 'Feinteile <20 mm',
        nonAluminium: 'Kein Aluminium',
        nonMetal: 'Kein Metall',
        otherMetal: 'Anderes Metall',
        prohibitive: 'Nicht zulässig',
        radiatorCondenser: 'Kühler/Kondensator',
        stainlessSteel: 'Edelstahl',
        taintTabor: 'Taint Tabor',
        total: 'Gesamt',
        ubcFoil: 'UBC/Folie',
      },
    },
    zorbaMaterialSampleBreakdown: {
      labels: {
        aluminiumUbc: 'Aluminium - UBC',
        compositeMaterials: 'Verbundmaterialien',
        copperBrassRedsYellows: "Kupfer & Messing - 'Rottöne & Gelbtöne'",
        finesLt20mm: 'Feinteile <20 mm',
        nonFerrousMetalWhites: "Nichteisenmetall - 'Weiße'",
        nonTarget: 'Kein Zielmaterial',
        otherMetalFerrous: 'Anderes Metall (eisenhaltig)',
        otherNonMetal: 'Anderes Nicht-Metall',
        postConsumerAluminiumPackaging: 'Aluminiumverpackungen aus dem Post-Consumer-Bereich',
        prohibitive: 'Nicht zulässig',
        targetMaterial: 'Zielmaterial',
        total: 'Gesamt',
      },
    },
  },
  cameraGuidance: {
    camera: {
      notifications: {
        autoCaptureOff: 'Automatische Aufnahme aus',
        autoCaptureOn: 'Automatische Aufnahme an',
      },
      rejectedPhotos: {
        message: 'Tippe unten auf das Foto, um den Fehlergrund zu sehen. Lösche das Foto danach und versuche es erneut',
        photo: 'Foto',
        photos: 'Fotos',
        title: '{{prefix}}, um {{count}} {{countLabel}} zu prüfen!',
        titlePrefixObfuscated: 'Fehlgeschlagen',
        titlePrefixWithAi: 'Die KI konnte',
      },
    },
    cameraFooter: {
      actions: {
        capture: 'Aufnehmen',
        complete: 'Abschließen',
        guide: 'Leitfaden',
      },
      tooltips: {
        takePhotoToComplete: 'Nimm ein Foto auf, um abzuschließen',
      },
    },
    cameraHeader: {
      autoCapture: 'Automatische Aufnahme',
      flashAriaLabel: 'Blitz umschalten',
      confirmationDialog: {
        actions: {
          complete: 'Abschließen',
          exitAnyway: 'Trotzdem beenden',
        },
        messageWithAiLabel:
          'Deine Fotos werden gespeichert, aber du siehst die KI-Ergebnisse nicht sofort. Tippe auf Abschließen, um den KI-Bericht jetzt anzuzeigen.',
        messageWithoutAiLabel:
          'Deine Fotos werden gespeichert, aber du siehst die Ergebnisse nicht sofort. Tippe auf Abschließen, um den Bericht jetzt anzuzeigen.',
        title: 'Kamera verlassen?',
      },
      settings: {
        description:
          'Verwende den Offline-Modus, wenn deine Datenverbindung schwach ist, damit keine Fotos verloren gehen',
        title: 'Einstellungen',
      },
    },
    countdownOverlay: {
      snap: 'Klick!',
    },
    previewImageReview: {
      actions: {
        deleteAndRetake: 'Löschen und neu aufnehmen',
      },
      evidencePhotoAlt: 'Nachweisfoto',
    },
  },
  charts: {
    targetBaleWeightsHistogram: {
      axes: {
        x: 'Vorhergesagter Zielmaterialanteil %',
        y: 'Anzahl Ballen',
      },
      labels: {
        average: 'Durchschnitt: {{targetGrade}}%',
        bales: 'Ballen',
      },
      tooltip: {
        label: 'Erkannter %: {{label}}',
        value: '{{value}} Ballen',
      },
    },
  },
  dataCards: {
    aiLooseAnalysis: {
      description:
        'Ein vereinfachter Probenprozess mit von Safi AI geschätzten Gewichten. Werte werden auf die nächsten 0,01 kg gerundet.',
      latestInspectionTitle: 'Neueste Inspektion',
      showMediaLabel: 'Medien anzeigen',
      title: 'KI-Lose-Analyse',
    },
    aluminiumComposition: {
      reportTitle: 'Bericht',
      targetDistributionTitle: 'Zielverteilung',
    },
    common: {
      loads: 'Ladungen',
      metricTonnes: 'Metrische Tonnen',
    },
    hdpeComposition: {
      aiBaleAnalysisTitle: 'KI-Ballenanalyse',
      reportTitle: 'Bericht',
    },
    hdpeCompositionQualityReport: {
      actions: {
        showMedia: 'Medien anzeigen',
      },
      description:
        '<bold>HDPE-Gewichtsanalyse</bold> zeigt den prozentualen Materialanteil in einer Reihe historisch von Safi-Inspektoren geprüfter Ballen.',
      fields: {
        latestInspection: 'Neueste Inspektion',
        supplierBalesAnalysed: 'Analysierte Ballen des Lieferanten',
      },
      title: 'KI-Ballenanalyse',
    },
    loadingDetails: {
      actions: {
        showMedia: 'Medien anzeigen',
      },
      fields: {
        loadingNotes: 'Verladenotizen',
        loadingTechnique: 'Verladetechnik',
        packedOn: 'Verpackt am',
        targetPayload: 'Zielnutzlast',
      },
      title: 'Verladedetails',
      values: {
        targetPayloadMt: '{{targetPayload}} (MT)',
      },
    },
    materialNotAnalysed: {
      analysisTitle: 'Analyse',
      noResultsDescription:
        'Wir haben für diesen speziellen Materialtyp noch kein Modell entwickelt. Mit mehr Daten werden wir in Zukunft Analysen bereitstellen können.',
      noResultsTitle: 'Keine Ergebnisse verfügbar',
      reportTitle: 'Bericht',
    },
    materialSpecifications: {
      actions: {
        showMedia: 'Medien anzeigen',
      },
      analysis: {
        completed: 'Abgeschlossen',
        notCompleted: 'Nicht abgeschlossen',
      },
      fields: {
        baleDimensions: 'Ballenabmessungen',
        capsAndLabels: 'Deckel und Etiketten',
        density: 'Dichte (kg/m³)',
        ferrousCheck: 'Eisenmetallprüfung',
        foodGrade: 'Lebensmitteltauglich',
        materialAnalysisNotes: 'Notizen zur Materialanalyse',
        materialForm: 'Materialform',
        materialProcess: 'Materialprozess',
        origin: 'Herkunft',
        radiationCheck: 'Strahlenprüfung',
        strapping: 'Umreifung',
        visualCheck: 'Sichtprüfung',
      },
      title: 'Materialspezifikationen',
      units: {
        meters: 'm',
      },
      values: {
        no: 'Nein',
        yes: 'Ja',
      },
    },
    petComposition: {
      aiBaleAnalysisTitle: 'KI-Ballenanalyse',
      reportTitle: 'Bericht',
    },
    petCompositionQualityReport: {
      actions: {
        showMedia: 'Medien anzeigen',
      },
      description:
        '<bold>PET-Gewichtsanalyse</bold> zeigt den prozentualen Materialanteil in einer Reihe historisch von Safi-Inspektoren geprüfter Ballen.',
      fields: {
        latestInspection: 'Neueste Inspektion',
        supplierBalesAnalysed: 'Analysierte Ballen des Lieferanten',
      },
      title: 'KI-Ballenanalyse',
    },
    site: {
      actions: {
        showMedia: 'Medien anzeigen',
      },
      fields: {
        machinesUsed: 'Verwendete Maschinen',
        materialOrigin: 'Materialherkunft',
        monthlyVolume: 'Monatsvolumen',
        siteGround: 'Standortboden',
        siteNotes: 'Standortnotizen',
        storageAndCover: 'Lagerung & Abdeckung',
      },
      monthlyVolumeValue: '{{productionVolume}} (MT)',
      title: 'Standort',
    },
    targetBaleWeights: {
      actions: {
        showMedia: 'Medien anzeigen',
      },
      attachmentDetails: {
        foodCan: 'Lebensmitteldose',
        nonMetal: 'Nicht-Metall',
        otherAluminium: 'Anderes Aluminium',
        otherMetal: 'Anderes Metall',
        title: 'Details zu Anhaftungen',
      },
      callouts: {
        attachmentWeightContent: 'Anteil Anhaftungsgewicht',
        averageUbcWeightContent: 'Durchschnittlicher UBC-Gewichtsanteil',
      },
      evidencePhotoAlt: 'Nachweisfoto',
      inspectorsGatheredMessage:
        'Inspektoren haben Daten aus der Analyse von <bold>{{count}}</bold> Ballen aus <bold>{{from}}</bold> und <bold>{{to}}</bold> gesammelt',
      inspectorsGatheredPrefix: 'Inspektoren haben Daten aus der Analyse gesammelt',
      showBaleQualityData: 'Ballenqualitätsdaten anzeigen',
      tabs: {
        bestBales: 'Beste Ballen',
        typicalBales: 'Typische Ballen',
        ubcSuffix: 'UBC',
      },
      title: 'KI-Ballenanalyse',
    },
    tradeCompanySessionLoose: {
      aiLooseAnalysisTitle: 'KI-Lose-Analyse',
      reportTitle: 'Bericht',
      sampleTotalWeightTitle: 'Gesamtgewicht der Probe',
    },
    transactionHistory: {
      alerts: {
        newSupplier: 'Neuer Lieferant! Du hast zuvor noch nicht bei ihm gekauft.',
        purchasedBefore: 'Du hast bereits zuvor bei diesem Lieferanten gekauft!',
      },
      title: 'Transaktionsverlauf',
      total: {
        description: 'Transaktionen zwischen diesem Lieferanten und anderen Käufern in den letzten 6 Monaten.',
      },
    },
    weightSampleAnalysis: {
      actions: {
        showMedia: 'Medien anzeigen',
      },
      description: {
        withTotalWeight:
          'Die Daten basieren auf der neuesten manuellen Probe von {{totalWeightInKg}} des Ausgangsmaterials des Lieferanten, die von einem Safi-Inspektor erstellt wurde. Werte werden auf die nächsten 0,01 kg gerundet.',
        withoutTotalWeight: 'Werte werden auf die nächsten 0,01 kg gerundet.',
      },
      labels: {
        latestInspection: 'Neueste Inspektion',
      },
      title: 'Analyse der manuellen Probe',
    },
  },
  evidence: {
    actionBar: {
      deletePrompt: 'Möchtest du die ausgewählten Nachweise wirklich löschen?',
      deleteTitle: '{{evidence}} löschen',
      selectedCount: '{{selected}} ausgewählt',
    },
    chips: {
      ai: 'KI',
      selfServe: 'Self-Serve',
    },
    defaultBreakdownFormFields: {
      actions: {
        addBreakdownItem: 'Aufschlüsselungseintrag hinzufügen',
      },
      labels: {
        name: 'Name',
        unit: 'Einheit',
        value: 'Wert',
      },
      title: 'Aufschlüsselung',
    },
    editForm: {
      actions: {
        delete: 'Löschen',
        edit: 'Bearbeiten',
      },
      labels: {
        newEvidenceType: 'Neuer {{evidence}}-Typ',
        newLabel: 'Neu',
        typeLabel: 'Typ',
      },
      notifications: {
        deleteError: 'Fehler beim Löschen des Nachweises',
        deleteSuccess: 'Nachweis gelöscht',
        updateError: 'Fehler beim Aktualisieren des Nachweises',
        updateSuccess: 'Nachweis aktualisiert',
      },
      validation: {
        typeOrSiteMaterialRequired: 'Entweder der Nachweistyp oder das Standortmaterial muss angegeben werden',
      },
      warnings: {
        deleteMessage:
          'Du bist dabei, diesen Nachweis von diesem Standortmaterial zu löschen. Dadurch wird er aus dem zugehörigen Qualitätsbericht entfernt. Bitte bestätige, um fortzufahren.',
        editMessage:
          'Du bist dabei, diesen Nachweis zu ändern. Dadurch wird er aus dem zugehörigen Qualitätsbericht entfernt. Bitte bestätige, um fortzufahren.',
        title: 'Warnung!',
      },
    },
    inspectionSessionDetails: {
      labels: {
        dealId: 'Deal-ID:',
        inspectionSlug: 'Inspektions-Slug:',
      },
      tooltips: {
        reportAvailableOnCompletion: 'Sobald die Inspektion abgeschlossen ist, findest du hier einen Link zum Bericht.',
        searchInDrive: 'Inspektion in Google Drive suchen',
        viewDealDetails: 'Deal-Details anzeigen',
        viewExternalReport: 'Externen Bericht anzeigen',
      },
    },
    offlineGallery: {
      notifications: {
        clearError: 'Offline-Nachweise konnten nicht gelöscht werden',
        clearSuccess: 'Offline-Nachweise erfolgreich gelöscht',
      },
      title: 'Offline-{{evidenceLower}}',
      tooltips: {
        cannotSyncWhileOffline: 'Synchronisierung im Offline-Modus nicht möglich',
        clear: 'Offline-Nachweise löschen',
        sync: 'Offline-Nachweise synchronisieren',
        syncInProgress: 'Synchronisierung läuft...',
      },
    },
    sampleBreakdown: {
      labels: {
        aiBreakdown: 'KI-Aufschlüsselung',
        manualBreakdown: 'Manuelle Aufschlüsselung',
        noNotes: 'Keine Notizen',
        sample: 'Probe',
        sampleEvidence: 'Probennachweise',
      },
      totalWeight: '{{totalWeight}}kg',
    },
    sampleFormFields: {
      breakdowns: {
        alu: {
          groups: {
            aluminium: 'Aluminium',
            nonAluminium: 'Nicht-Aluminium',
          },
          items: {
            foilAndTray: 'Folie und Schale',
            foodCan: 'Lebensmitteldose',
            nonMetal: 'Nicht-Metall',
            otherAluminium: 'Anderes Aluminium',
            otherAluminiumAerosols: 'Anderes Aluminium (Aerosole)',
            otherMetal: 'Anderes Metall',
            rigidPlastics: 'Starre Kunststoffe',
            ubc: 'UBC',
            vapes: 'Vapes',
          },
        },
        pet: {
          groups: {
            otherPetAndPlastics: 'Anderes PET & Kunststoffe',
            petBottles: 'PET-Flaschen',
          },
          items: {
            clear: 'Klar',
            clearFullSleeve: 'Klar (Vollflächige Sleeve)',
            clearLarge: 'Klar (Groß)',
            clearPetTray: 'Klare PET-Schale',
            darkBlue: 'Dunkelblau',
            film: 'Folie',
            green: 'Grün',
            lightBlue: 'Hellblau',
            liquid: 'Flüssigkeit',
            nonFoodGradeBottle: 'Nicht-lebensmitteltaugliche PET-Flasche',
            nonPlastic: 'Nicht-Kunststoff',
            other: 'Sonstiges',
            otherPlastic: 'Anderer Kunststoff',
            prohibitive: 'Nicht zulässig',
          },
        },
        taintTabor: {
          groups: {
            aluWithAttachments: 'Alu. mit Anhaftungen',
            aluminium: 'Aluminium',
            nonAluminium: 'Nicht-Aluminium',
          },
          items: {
            aluIron: 'Alu./Eisen',
            aluNonMetal: 'Alu./Nicht-Metall',
            cast: 'Guss',
            castIron: 'Guss/Eisen',
            ferrousMetal: 'Eisenmetall',
            finesLt20mm: 'Feinteile <20mm',
            nonMetal: 'Nicht-Metall',
            otherMetal: 'Anderes Metall',
            prohibitive: 'Nicht zulässig',
            radiatorCondenser: 'Kühler/Kondensator',
            stainlessSteel: 'Edelstahl',
            taintTabor: 'Taint Tabor',
            ubcFoil: 'UBC/Folie',
          },
        },
        zorba: {
          groups: {
            nonTarget: 'Kein Zielmaterial',
            targetMaterial: 'Zielmaterial',
          },
          items: {
            aluminiumFoilUbc: 'Aluminium - Folie / UBC',
            compositeMaterials: 'Verbundmaterialien',
            copperAndBrass: "Kupfer & Messing 'Rottöne & Gelbtöne'",
            finesLt20mm: 'Feinteile <20mm',
            nonFerrousMetal: "Nichteisenmetall 'Weiße'",
            otherMetalFerrous: 'Anderes Metall (eisenhaltig)',
            otherNonMetal: 'Anderes Nicht-Metall',
            postConsumerAluminiumPackaging: 'Aluminiumverpackungen aus dem Post-Consumer-Bereich',
            prohibitive: 'Nicht zulässig',
          },
        },
      },
      labels: {
        notes: 'Notizen',
        totalWeight: 'Gesamtgewicht',
        weightUnit: 'Kg',
      },
    },
    sampleMaterialEditForm: {
      actions: {
        edit: 'Bearbeiten',
      },
      warning: {
        editMessage:
          'Du bist dabei, diesen Nachweis zu ändern. Dadurch wird er aus dem zugehörigen Qualitätsbericht entfernt. Bitte bestätige, um fortzufahren.',
        title: 'Warnung!',
      },
    },
    sampleSessionDetails: {
      manualSampleAnalysisTitle: 'Analyse der manuellen Probe',
      noData: 'Keine Daten',
      sampleImagesTitle: 'Probenbilder',
    },
    sessionStats: {
      excludedSubtitle: 'Bilder haben unsere Anforderungsregeln nicht bestanden',
      excludedTitle: 'Ausgeschlossen',
      includedSubtitle: 'Bilder haben unsere Anforderungsregeln bestanden',
      includedTitle: 'Enthalten',
    },
    unloadingPhotoUploader: {
      label: 'Fotos hochladen',
      notifications: {
        uploadFailed: 'Hochladen fehlgeschlagen',
        uploadSuccess: 'Hochladen erfolgreich',
      },
    },
  },
  helpGuides: {
    aiBale: {
      partTwo: {
        captions: {
          clearWhichBaleToFocusOn: 'Klar, auf welchen Ballen fokussiert wird',
          goodQualityPhoto: 'Foto in guter Qualität',
          goodQualityPhotoAndDistanceFromBale: 'Foto in guter Qualität und guter Abstand zum Ballen',
          greatDistanceFromBale: 'Sehr guter Abstand zum Ballen',
          materialNotTooTightlyPacked: 'Material ist nicht zu dicht gepackt',
          materialTooFlatAndTightlyPacked: 'Material ist zu flach und zu dicht gepackt',
          nearlyAllBaleFaceShown: 'Fast die gesamte Ballenfläche ist sichtbar',
          photoTooBlurry: 'Foto ist zu unscharf',
          someBackgroundIsOk: 'Etwas Hintergrund ist in Ordnung',
          tooClose: 'Zu nah',
          tooManyBalesAngleNotStraightPoorQuality: 'Zu viele Ballen, Winkel nicht frontal, schlechte Qualität',
          tooManyBalesManySidesVisible: 'Zu viele Ballen, viele Seiten sichtbar',
          tooManyBalesTooFarAway: 'Zu viele Ballen, zu weit entfernt',
          unclearWhichIsMainBale: 'Unklar, welcher der Hauptballen ist',
        },
        intro: 'Hier sind Beispiele für gute und schlechte Fotos:',
      },
    },
    aiBriquette: {
      partOne: {
        imageAlt: 'Max. 0,5 Meter entfernt stehen',
        instructions: '<bold>Stehe 0,5 Meter</bold> vor dem Brikett.',
      },
      partTwo: {
        analysisInstruction: 'Bei der Analyse einer Palette <bold>mache Fotos von mind. 6 Briketts.</bold>',
        captions: {
          goodDistance: 'Guter Abstand',
          markingsVisible: 'Markierungen sichtbar',
          noMarkingsVisible: 'Keine Markierungen sichtbar',
          okDistance: 'OK-Abstand',
          tooDirty: 'Zu schmutzig',
          tooShredded: 'Zu stark zerkleinert',
        },
        intro: 'Hier sind Beispiele für gute und schlechte Fotos:',
      },
    },
    aiLoose: {
      partTwo: {
        captions: {
          cannotSeeAllMaterial: 'Nicht das gesamte Material sichtbar',
          floorHasLargeMarkings: 'Boden hat große Markierungen',
          materialInsideBlackBox: 'Material befindet sich innerhalb der schwarzen Box',
          materialNotOnGroundOrTable: 'Material liegt nicht auf Boden/Tisch',
          materialShouldNotTouchPurpleLines: 'Das Material sollte die violetten Linien nicht berühren',
          materialSpreadVeryWell: 'Das Material ist sehr gut verteilt',
          materialSpreadWell: 'Das Material ist gut verteilt',
          materialTouchesEdge: 'Das Material berührt den Rand',
          shadow: 'Schatten',
          squareFullOfMaterial: 'Das Quadrat ist voll mit Material',
          tooHeapedAndTooBig: 'Zu hoch aufgeschüttet und zu groß',
        },
        intro: 'Hier sind Beispiele für gute und schlechte Fotos:',
      },
    },
    alert: {
      actions: {
        startGuide: 'Leitfaden starten - 1 Min.',
      },
      description: {
        obfuscatedAi: 'Lies diesen kurzen Leitfaden, um die richtigen Fotos zu machen.',
        withAi:
          'Lies diesen kurzen Leitfaden, um die richtigen Fotos für präzise KI-Ergebnisse zu machen. Schlechte Fotos = schlechte Ergebnisse.',
      },
      title: 'Zum ersten Mal {{modelTitle}} verwenden?',
    },
    autoCaptureTip: {
      message:
        'Profi-Tipp! Arbeite schneller und behalte deine Handschuhe an mit <autoCapture>Automatische Aufnahme</autoCapture>, die Fotos automatisch für dich macht.',
    },
    common: {
      actions: {
        back: 'Zurück',
        finish: 'Fertig',
        next: 'Weiter',
        skipGuide: 'Leitfaden überspringen',
      },
      models: {
        bale: 'Ballen',
        briquette: 'Brikett',
      },
    },
    complete: {
      message:
        'Wenn du fertig bist, tippe in der Kamera auf <complete>Abschließen</complete>. Danach siehst du den <qualityReport>Qualitätsbericht.</qualityReport>',
    },
    dialog: {
      stepLabel: 'Schritt {{currentStep}}/{{maxSteps}}',
      title: 'Willkommen bei {{modelTitle}}!',
    },
    guideTitle: {
      obfuscatedAi: 'Lerne, wie du Fotos richtig aufnimmst, um die besten Ergebnisse zu erhalten.',
      withAi: 'Lerne, wie du Fotos richtig aufnimmst, um die besten KI-Ergebnisse zu erhalten.',
    },
    offlineWarning: {
      message:
        'Profi-Tipp! Aktiviere den <offlineMode>Offline-Modus</offlineMode>, damit keine Fotos verloren gehen, wenn deine Datenverbindung schwach ist.',
    },
    reviewDeleteHelpText: {
      withAi:
        'Du kannst sehen, ob ein {{model}} von der KI <accepted>akzeptiert</accepted> oder <rejected>abgelehnt</rejected> wurde. <delete>Lösche</delete> und <retake>nimm fehlerhafte Fotos neu auf</retake>.',
      withoutAi:
        'Du kannst sehen, ob ein {{model}} <accepted>akzeptiert</accepted> oder <rejected>abgelehnt</rejected> wurde. <delete>Lösche</delete> und <retake>nimm fehlerhafte Fotos neu auf</retake>.',
    },
    reviewDeleteTip: {
      message:
        'Top-Tipp! Du kannst im Qualitätsbericht auch <reviewDelete>prüfen und löschen</reviewDelete> sowie <addMorePhotos>weitere Fotos hinzufügen</addMorePhotos>.',
    },
  },
  instructions: {
    photoInstructionsDrawer: {
      actions: {
        doNotShowAgain: 'Nicht erneut anzeigen',
        takePhoto: 'Foto aufnehmen',
      },
      description:
        'Unsere KI analysiert hochgeladene Bilder anhand mehrerer Parameter. Deine hochgeladenen Bilder sollten wie unten aussehen.',
      frontFaceDescription:
        'Die häufigste Ursache für ein ungültiges Bild ist die Ballenausrichtung. Die Objekte im Ballen sollten flachgedrückt und frontal ausgerichtet sein.',
      frontFaceTitle: 'Was ist die Vorderseite?',
      imageAlt: {
        balePhotoExample: 'Beispiel für ein Ballenfoto',
        frontFaceBalePhoto: 'Ballenfoto der Vorderseite',
        treeRingBalePhoto: 'Ballenfoto mit Baumringansicht',
      },
      labels: {
        objectsFlattenedFaceOn: 'Objekte abgeflacht und frontal',
        objectsSquashedSideOn: 'Objekte zusammengedrückt und seitlich',
      },
      title: 'Bilder aufnehmen',
    },
  },
  materialComponents: {
    supportedComponent: {
      labels: {
        materialComponent: 'Materialkomponente',
      },
    },
    supportedComponentValue: {
      adornments: {
        max: 'Max: ',
        min: 'Min: ',
      },
      labels: {
        booleanValue: 'Boolescher Wert',
        value: 'Wert',
      },
    },
  },
  navigation: {
    sidebar: {
      closeAriaLabel: 'Schließen',
    },
    userMenu: {
      language: 'Sprache',
      logOut: 'Abmelden',
      manageCompany: 'Unternehmen verwalten',
      notifications: 'Benachrichtigungseinstellungen',
      qualityDigest: 'Tägliche Qualitäts-E-Mail',
    },
  },
  networkStatus: {
    alert: {
      offlineMessage: 'Du bist derzeit offline. Nachweise werden lokal gespeichert, bis du wieder online bist.',
    },
    override: {
      label: 'Offline-Modus',
      notifications: {
        disabled: 'Offline-Modus deaktiviert',
        enabled: 'Offline-Modus aktiviert',
      },
    },
  },
  omnisearch: {
    noOptions: {
      withValue: 'Keine Ergebnisse gefunden',
      withoutValue: 'Zum Suchen tippen',
    },
    placeholder: {
      desktop: 'Ctrl/Cmd + K zum Suchen',
      mobile: 'Suchen',
    },
  },
  qualityReport: {
    footer: {
      backToSiteQualityPage: 'Zurück zur Qualitätsseite des Standorts',
    },
    link: {
      buttonLabel: 'Qualitätsbericht',
      tooltip: {
        noMaterialSelected: 'Kein Material ausgewählt',
        notEnoughEvidence: 'Nicht genügend Nachweise für das ausgewählte Material',
      },
    },
    page: {
      notAvailable: 'Qualitätsbericht nicht verfügbar',
    },
    sampleControls: {
      actions: {
        viewData: 'Daten anzeigen',
      },
    },
    sampleSelect: {
      label: 'Prüfungsdatum',
    },
    share: {
      content: 'Hier ist der Link zum Qualitätsbericht von {{brandName}}: {{url}}',
      labels: {
        copyLink: 'Link kopieren',
        copyNotification: 'In die Zwischenablage kopiert',
        download: 'Herunterladen',
        email: 'E-Mail',
        systemShare: 'System teilen',
        trigger: 'Teilen',
        triggerAriaLabel: 'Teilen',
        whatsapp: 'WhatsApp',
      },
      subject: '{{brandName}} Qualitätsbericht',
    },
  },
  siteMaterial: {
    tradeCompanySiteMaterialPicker: {
      actions: {
        addNewMaterial: 'Neüs Material hinzufügen',
        addNewMaterialAriaLabel: 'Neüs Material hinzufügen',
      },
      labels: {
        company: 'Unternehmen',
        siteMaterial: 'Standortmaterial',
      },
    },
  },
} satisfies I18nDictionaryShape<typeof featuresEnGb>;
